<template>
    <div class="slider-with-input">
        <div class="slider-with-input__slider-wrapper flex-order-handle">
            <slider
                :value="value == null ? 0 : value"
                :min="min"
                :max="maxSlider"
                :step="step"
                @input="onSliderInput"
                @change="onSliderChange" />
        </div>
        <div class="slider-with-input__constraint flex-order-handle">
            <span class="slider-with-input__constraint--left">{{ textleft }}</span>
            <span class="slider-with-input__constraint--right">{{ textright }}</span>
        </div>
        <div class="row d-flex align-items-center flex-order-handle">
            <div class="d-lg-none col-6">
                <span>{{ mobileText }}</span>
            </div>
            <div class="col-6 col-lg-12">
                <input
                    class="slider-with-input__input form-control"
                    :placeholder="placeholder"
                    :inputmode="inputMode"
                    :pattern="inputPattern"
                    v-model.lazy="displayValue"
                    @blur="onBlur"
                    @focus="onFocus"
                    @keyup.enter="$refs.slider.blur()"
                    @click="handleClick"
                    ref="slider">
            </div>
        </div>
        <p class="d-none d-lg-block slider-with-input__text">
            {{ text }}
        </p>
    </div>
</template>

<script>
import Slider from '@/components/common/Slider';

export default {
    name: 'SliderWithInput',
    components: {
        Slider,
    },
    data () {
        return {
            isInputActive: false,
        };
    },
    props: {
        value: {
            type: Number,
            required: false,
            default: null,
        },
        min: {
            type: Number,
            required: true,
        },
        max: {
            type: Number,
            required: true,
        },
        maxSlider: {
            type: Number,
            required: true,
        },
        inputPrefix: {
            type: String,
            required: false,
            default: '',
        },
        inputSuffix: {
            type: String,
            required: false,
            default: '',
        },
        text: {
            type: String,
            required: true,
        },
        textleft: {
            type: String,
            required: false,
            default: '',
        },
        textright: {
            type: String,
            required: false,
            default: '',
        },
        placeholder: {
            type: String,
            required: false,
            default: '-',
        },
        inputFormatter: {
            type: Object,
            required: false,
            default () {
                return {};
            },
        },
        step: {
            type: Number,
            required: false,
            default: 1,
        },
        mobileText: {
            type: String,
            required: true,
            default: '',
        },
        forceNumericKeyboard: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    computed: {
        displayValue: {
            get () {
                if (this.value) {
                    if (this.isInputActive) {
                        return this.value.toString();
                    } else {
                        return this.format(this.value);
                    }
                }
                return this.value;
            },
            set (modifiedValue) {
                let unformattedNewValue = Number(this.unformat(modifiedValue));
                if (isNaN(unformattedNewValue)) {
                    unformattedNewValue = undefined;
                } else {
                    unformattedNewValue = this.normalizeValue(unformattedNewValue);
                }
                this.$emit('valueChangeConfirmed', unformattedNewValue);
            },
        },
        inputPattern () {
            return this.forceNumericKeyboard ? '[0-9]*' : '';
        },
        inputMode () {
            return this.forceNumericKeyboard ? 'numeric' : '';
        },
    },
    methods: {
        onFocus () {
            this.isInputActive = true;
        },
        onBlur () {
            this.isInputActive = false;
        },
        onSliderChange (newValue) {
            const normalizedValue = this.normalizeValue(newValue);
            this.$emit('valueChangeConfirmed', normalizedValue);
        },
        onSliderInput (newValue) {
            const normalizedValue = this.normalizeValue(newValue);
            this.$emit('valueChange', normalizedValue);
        },
        normalizeValue (value) {
            if (value < this.min) {
                return this.min;
            }
            if (this.max && value > this.max) {
                return this.max;
            }
            return Math.round(value / this.step) * this.step;
        },
        unformat (value) {
            return Number(value.replace(/[^\d]/g, ''));
        },
        format (value) {
            if (value) {
                if (this.inputFormatter.thousands) {
                    value = value.toFixed(0).replace(/(\d)(?=(\d{3})+(?:\.\d+)?$)/g, '$1' + this.inputFormatter.thousands);
                }
                if (this.inputFormatter.suffix) {
                    value = value + this.inputFormatter.suffix;
                }
                if (this.inputFormatter.prefix) {
                    value = this.inputFormatter.prefix + value;
                }
            }
            return value;
        },
        handleClick (event) {
            event.target.select();
        },
    },
};
</script>

<style scoped lang="scss">
.slider-with-input {
  @include media-breakpoint-up(lg) {
    padding: 0 40px 0 0;
  }
  @include media-breakpoint-down(xs) {
    display: flex;
    flex-direction: column;

    .flex-order-handle:nth-child(1) {
      order: 2;
    }
    .flex-order-handle:nth-child(2) {
      order: 3;
    }
    .flex-order-handle:nth-child(3) {
      order: 1;
    }
  }
}

.slider-with-input__constraint {
  @include font-help();
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;

  @include media-breakpoint-down(xs) {
    margin-bottom: 10px;
  }
}

.slider-with-input__slider-wrapper {
  margin-bottom: 15px;

  @include media-breakpoint-down(xs) {
    margin-top: 5px;
    margin-bottom: 5px;
  }
}

.slider-with-input__text {
  @include font-help();
  margin-top: 5px;
  margin-bottom: 20px;
}

// Disable HTML validation error highlighting (Chrome / FF)
.slider-with-input__input:invalid {
  box-shadow: none;
}
</style>
